import React, { useEffect } from "react";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import { lightTheme, darkTheme } from "../styles/theme";
import { GlobalStyles } from "../styles/GlobalStyles";
import Navigation from "./Navigation";
import { FaHome, FaArrowLeft } from "react-icons/fa";
import { RandomFloatingIcons, BACKEND_ICONS } from "./TechStackIcons";

const NotFoundSection = styled.section<{ theme: "light" | "dark" }>`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 100px 5% 40px;
  background: ${({ theme }) =>
    theme === "light" ? lightTheme.background : darkTheme.surface};
  position: relative;
  overflow: hidden;

  /* Cross-hatch pattern - matching Contact section */
  background-image: ${({ theme }) =>
    theme === "light"
      ? `
        repeating-linear-gradient(
          45deg,
          transparent,
          transparent 10px,
          rgba(77, 159, 255, 0.18) 10px,
          rgba(77, 159, 255, 0.18) 12px
        ),
        repeating-linear-gradient(
          -45deg,
          transparent,
          transparent 10px,
          rgba(77, 159, 255, 0.18) 10px,
          rgba(77, 159, 255, 0.18) 12px
        )
      `
      : `
        repeating-linear-gradient(
          45deg,
          transparent,
          transparent 10px,
          rgba(255, 255, 255, 0.03) 10px,
          rgba(255, 255, 255, 0.03) 11px
        ),
        repeating-linear-gradient(
          -45deg,
          transparent,
          transparent 10px,
          rgba(255, 255, 255, 0.03) 10px,
          rgba(255, 255, 255, 0.03) 11px
        )
      `};

  /* Central accent glow */
  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 50%;
    height: 50%;
    background: ${({ theme }) =>
      theme === "light"
        ? `radial-gradient(ellipse at center, rgba(77, 159, 255, 0.15), transparent 70%)`
        : `radial-gradient(ellipse at center, ${darkTheme.accent}12, transparent 70%)`};
    pointer-events: none;
    z-index: 0;
  }

  @media (min-width: 768px) {
    padding: 120px 10% 60px;
  }
`;

const ContentWrapper = styled(motion.div)`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  text-align: center;
`;

const ErrorCode = styled(motion.div)<{ theme: "light" | "dark" }>`
  font-size: 10rem;
  font-weight: 900;
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.accent : darkTheme.accent};
  line-height: 1;
  margin-bottom: 1.5rem;
  text-shadow: ${({ theme }) =>
    theme === "light"
      ? `8px 8px 0 ${lightTheme.border}`
      : `8px 8px 0 ${darkTheme.border}`};
  letter-spacing: -0.05em;
  display: inline-block;
  border: 6px solid
    ${({ theme }) => (theme === "light" ? lightTheme.border : darkTheme.border)};
  padding: 20px 40px;
  background: ${({ theme }) =>
    theme === "light" ? lightTheme.background : darkTheme.background};
  box-shadow: ${({ theme }) =>
    theme === "light" ? lightTheme.shadowLarge : darkTheme.shadowLarge};
  transform: rotate(-2deg);

  @media (min-width: 768px) {
    font-size: 12rem;
    padding: 30px 60px;
  }

  @media (min-width: 1024px) {
    font-size: 15rem;
  }
`;

const Title = styled(motion.h1)<{ theme: "light" | "dark" }>`
  font-size: 2rem;
  font-weight: 900;
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.text : darkTheme.text};
  margin: 2rem 0 1rem;
  text-transform: uppercase;
  letter-spacing: -0.02em;
  display: inline-block;
  padding: 12px 24px;
  background: ${({ theme }) =>
    theme === "light" ? lightTheme.accent : darkTheme.accent};
  border: 4px solid
    ${({ theme }) => (theme === "light" ? lightTheme.border : darkTheme.border)};
  box-shadow: ${({ theme }) =>
    theme === "light" ? lightTheme.shadowMedium : darkTheme.shadowMedium};
  transform: rotate(1deg);
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.background : darkTheme.background};

  @media (min-width: 768px) {
    font-size: 2.5rem;
    padding: 16px 32px;
  }
`;

const Description = styled(motion.p)<{ theme: "light" | "dark" }>`
  font-size: 1rem;
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.textSecondary : darkTheme.textSecondary};
  margin: 2rem auto;
  max-width: 600px;
  line-height: 1.6;
  font-weight: 500;

  @media (min-width: 768px) {
    font-size: 1.1rem;
  }

  strong {
    color: ${({ theme }) =>
      theme === "light" ? lightTheme.text : darkTheme.text};
    font-weight: 900;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  justify-content: center;
  margin-top: 2.5rem;

  @media (min-width: 768px) {
    gap: 25px;
    margin-top: 3rem;
  }
`;

const ActionButton = styled(motion.button)<{
  theme: "light" | "dark";
  primary?: boolean;
}>`
  padding: 16px 32px;
  font-size: 1rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: ${({ theme, primary }) =>
    primary
      ? theme === "light"
        ? lightTheme.accent
        : darkTheme.accent
      : "transparent"};
  color: ${({ theme, primary }) =>
    primary
      ? theme === "light"
        ? lightTheme.background
        : darkTheme.background
      : theme === "light"
        ? lightTheme.text
        : darkTheme.text};
  border: 4px solid
    ${({ theme }) => (theme === "light" ? lightTheme.border : darkTheme.border)};
  cursor: pointer;
  box-shadow: ${({ theme }) =>
    theme === "light" ? lightTheme.shadowMedium : darkTheme.shadowMedium};
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;

  &:hover {
    transform: translate(-4px, -4px);
    box-shadow: ${({ theme }) =>
      theme === "light"
        ? `12px 12px 0 ${lightTheme.border}`
        : `12px 12px 0 ${darkTheme.accent}`};
  }

  &:active {
    transform: translate(0, 0);
    box-shadow: ${({ theme }) =>
      theme === "light" ? lightTheme.shadowSmall : darkTheme.shadowSmall};
  }

  @media (min-width: 768px) {
    padding: 18px 36px;
    font-size: 1.05rem;
  }
`;

const HelpText = styled(motion.p)<{ theme: "light" | "dark" }>`
  margin-top: 3rem;
  padding: 20px 24px;
  background: ${({ theme }) =>
    theme === "light" ? lightTheme.accent : darkTheme.accent};
  border: 4px solid
    ${({ theme }) => (theme === "light" ? lightTheme.border : darkTheme.border)};
  box-shadow: ${({ theme }) =>
    theme === "light" ? lightTheme.shadowMedium : darkTheme.shadowMedium};
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.background : darkTheme.background};
  font-weight: 700;
  font-size: 0.95rem;
  transform: rotate(-1deg);

  @media (min-width: 768px) {
    font-size: 1rem;
    padding: 24px 32px;
  }

  .emoji {
    font-size: 1.3em;
    margin: 0 8px;
  }
`;

const NotFound: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "404 - Page Not Found | Thuan Trinh Van";

    // Add noindex meta tag for 404 page
    const metaNoIndex = document.createElement("meta");
    metaNoIndex.name = "robots";
    metaNoIndex.content = "noindex, nofollow";
    document.head.appendChild(metaNoIndex);

    return () => {
      document.title =
        "Thuan Trinh Van | Software Engineer - Web & Blockchain Developer";
      document.head.removeChild(metaNoIndex);
    };
  }, []);

  const handleGoHome = () => {
    navigate("/");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  return (
    <>
      <GlobalStyles />
      <Navigation />
      <NotFoundSection theme={theme}>
        {/* Floating Tech Stack Icons */}
        <RandomFloatingIcons theme={theme} icons={BACKEND_ICONS} count={5} />

        <ContentWrapper
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <ErrorCode
            theme={theme}
            initial={{ scale: 0.5, opacity: 0, rotate: -10 }}
            animate={{ scale: 1, opacity: 1, rotate: -2 }}
            transition={{
              duration: 0.6,
              type: "spring",
              stiffness: 150,
              damping: 10,
            }}
          >
            404
          </ErrorCode>

          <Title
            theme={theme}
            initial={{ opacity: 0, y: 20, rotate: -5 }}
            animate={{ opacity: 1, y: 0, rotate: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Oops! Page Not Found
          </Title>

          <Description
            theme={theme}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            The page you're looking for seems to have wandered off into the
            digital void. <strong>Don't worry!</strong> Even the best developers
            encounter broken links sometimes. Let's get you back on track.
          </Description>

          <ButtonGroup>
            <ActionButton
              theme={theme}
              primary
              onClick={handleGoHome}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <FaHome />
              Back to Home
            </ActionButton>

            <ActionButton
              theme={theme}
              onClick={handleGoBack}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <FaArrowLeft />
              Go Back
            </ActionButton>
          </ButtonGroup>

          <HelpText
            theme={theme}
            initial={{ opacity: 0, scale: 0.9, rotate: 3 }}
            animate={{ opacity: 1, scale: 1, rotate: -1 }}
            transition={{ duration: 0.6, delay: 0.7 }}
          >
            <span className="emoji">💡</span>
            Tip: Try checking the URL for typos or return to the homepage to
            explore my projects, experience, and get in touch!
            <span className="emoji">🚀</span>
          </HelpText>
        </ContentWrapper>
      </NotFoundSection>
    </>
  );
};

export default NotFound;
